import MonthViewCalendar from "components/investorrelations/MonthViewCalendar";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import { NativeSelect } from "lib/miolo";
import { useId } from "react";
import { IIssuerType } from "types/graphqlTypes";
import CalendarNotes from "./CalendarNotes";
import type { IRightSideFiltersProps } from "./types";
import { EventsCalendarFilterRange, EventsCalenderFilterEventType } from "./types";
import { getFilterValuesBySelectOption } from "./utils";

export default function RightSideFilters({
  startDate,
  endDate,
  onChange,
  filterState,
  setFilterState,
  breakdown,
  isRenderInsidePortfolio,
  totalEvents,
}: IRightSideFiltersProps) {
  const viewer = useSelfie();
  const myPositionsCheckboxId = useId();
  const isInSP500CheckboxId = useId();
  const isInBookmarksCheckboxId = useId();
  const { issuerType, selectedFilter, eventType, myPositions, isInSP500, isInBookmarks } = filterState;

  function handleSetSelectedFilter(selectedOption: EventsCalendarFilterRange) {
    const { filterType, startDate, endDate } = getFilterValuesBySelectOption(selectedOption);

    setFilterState({
      ...filterState,
      startDate,
      endDate,
      selectedFilter: filterType,
    });
  }

  return (
    <div
      className={classNames("space-y-4 sm:sticky sm:top-24", {
        "sm:top-3": isRenderInsidePortfolio,
      })}
    >
      {!isRenderInsidePortfolio && viewer && (
        <>
          <div
            aria-checked={myPositions}
            className={classNames({
              "items-center rounded-md border bg-white p-4 tracking-wide shadow": true,
              "border-violet-300": myPositions,
              "border-transparent": !myPositions,
            })}
          >
            <div className="flex select-none items-center gap-x-2">
              <input
                id={myPositionsCheckboxId}
                type="checkbox"
                name="Minha Carteira"
                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                checked={myPositions}
                onChange={() => {
                  setFilterState({
                    ...filterState,
                    myPositions: !filterState?.myPositions,
                  });
                }}
              />

              <label
                className={classNames("cursor-pointer text-sm tracking-wider", {
                  "font-bold text-violet-500": myPositions,
                  "font-medium text-gray-500": !myPositions,
                })}
                htmlFor={myPositionsCheckboxId}
              >
                Minha Carteira
              </label>
            </div>
          </div>

          <div
            aria-checked={isInBookmarks}
            className={classNames({
              "items-center rounded-md border bg-white p-4 tracking-wide shadow": true,
              "border-violet-300": isInBookmarks,
              "border-transparent": !isInBookmarks,
            })}
          >
            <div className="flex select-none items-center gap-x-2">
              <input
                id={isInBookmarksCheckboxId}
                type="checkbox"
                name="Minha Carteira"
                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                checked={isInBookmarks}
                onChange={() => {
                  setFilterState({
                    ...filterState,
                    isInBookmarks: !filterState?.isInBookmarks,
                  });
                }}
              />

              <label
                className={classNames("cursor-pointer text-sm tracking-wider", {
                  "font-bold text-violet-500": isInBookmarks,
                  "font-medium text-gray-500": !isInBookmarks,
                })}
                htmlFor={isInBookmarksCheckboxId}
              >
                Favoritos
              </label>
            </div>
          </div>
        </>
      )}

      {!isRenderInsidePortfolio && issuerType !== IIssuerType.BrazilianCompany && (
        <div
          aria-checked={isInSP500}
          className={classNames({
            "space-y-2 rounded-md border bg-white p-4 tracking-wide shadow": true,
            "border-violet-300": isInSP500,
            "border-transparent": !isInSP500,
          })}
        >
          <div className="flex select-none items-center space-x-2">
            <input
              id={isInSP500CheckboxId}
              type="checkbox"
              name="SEP500"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              checked={isInSP500}
              onChange={() => {
                setFilterState({
                  ...filterState,
                  isInSP500: !filterState?.isInSP500,
                });
              }}
            />

            <label
              className={classNames("cursor-pointer text-sm tracking-wider", {
                "font-bold text-violet-500": isInSP500,
                "font-medium text-gray-500": !isInSP500,
              })}
              htmlFor={isInSP500CheckboxId}
            >
              S&P 500
            </label>
          </div>
        </div>
      )}

      {isRenderInsidePortfolio && (
        <NativeSelect
          height={48}
          value={eventType}
          onChange={(event) => {
            setFilterState({
              ...filterState,
              eventType: event.target.value as EventsCalenderFilterEventType,
            });
          }}
          style={{
            borderRadius: "8px",
          }}
        >
          <option value={EventsCalenderFilterEventType.All}>Todos</option>
          <option value={EventsCalenderFilterEventType.Earnings}>Balanços</option>
          <option value={EventsCalenderFilterEventType.Dividends}>Proventos</option>
          <option value={EventsCalenderFilterEventType.StockDividends}>Bonificação</option>
          <option value={EventsCalenderFilterEventType.RightsIssue}>Subscrição</option>
          <option value={EventsCalenderFilterEventType.StockSplit}>Desdobramento</option>
          <option value={EventsCalenderFilterEventType.SpinOff}>Cisão</option>
        </NativeSelect>
      )}

      <NativeSelect
        height={48}
        value={selectedFilter}
        onChange={(event) => handleSetSelectedFilter(event.target.value as EventsCalendarFilterRange)}
        style={{
          borderRadius: "8px",
        }}
      >
        <option value={EventsCalendarFilterRange.Today}>{`Hoje (${breakdown?.today ?? 0})`}</option>
        <option value={EventsCalendarFilterRange.InTheWeek}>{`Na Semana (${breakdown?.inTheWeek ?? 0})`}</option>
        <option value={EventsCalendarFilterRange.InTheMonth}>{`No Mês (${breakdown?.inTheMonth ?? 0})`}</option>
        <option value={EventsCalendarFilterRange.InTheLastMonth}>{`No Último Mês (${
          breakdown?.inTheLastMonth ?? 0
        })`}</option>
        <option value={EventsCalendarFilterRange.InTheNextMonth}>{`No Próximo Mês (${
          breakdown?.inTheNextMonth ?? 0
        })`}</option>
        <option value={EventsCalendarFilterRange.InTheNextNinetyDays}>{`Nos Próximos 90D (${
          breakdown?.inTheNextNinetyDays ?? 0
        })`}</option>
        <option value={EventsCalendarFilterRange.InTheYear}>{`No Ano (${breakdown?.inTheYear ?? 0})`}</option>
        {selectedFilter === EventsCalendarFilterRange.InSelectedPeriod && (
          <option value={EventsCalendarFilterRange.InSelectedPeriod}>Período Selecionado ({totalEvents})</option>
        )}
      </NativeSelect>

      <MonthViewCalendar startDate={startDate} endDate={endDate} onChange={onChange} />

      <CalendarNotes />
    </div>
  );
}
